import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { SharedModule } from '@/app/shared/shared.module';
import { TranslateModule } from '@ngx-translate/core';
import { InquiryKeys, InquiryTypes } from '@/app/shared/consts/comms-consts';
import { DkTabProps } from '../../../shared/components/dk-tabs/dk-tabs.component';
import { UserService } from '@/app/core/services/user.service';
import { InquiryProxy, InquiryViewModel } from '@/app/proxy/proxies';
import { SeedGenericIconComponent } from '@/app/components/icons/seed-generic-icon.component';
import { Popover } from 'primeng/popover';
import { UtcToLocalDatePipe, DateFormatOptions } from '@/app/shared/pipes/utc-to-local-date.pipe';
import { SkeletonComponent } from '@/app/features/shared/components/skeleton/skeleton.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { DateTime } from 'luxon';

@Component({
  selector: 'dk-support-inquiries',
  imports: [SharedModule, TranslateModule, SeedGenericIconComponent, UtcToLocalDatePipe, SkeletonComponent],
  templateUrl: './inquiries.component.html',
  styleUrl: './inquiries.component.less',
  providers: [InquiryProxy],
})
export class InquiriesComponent {
  @ViewChild('deleteChatPopover') deleteChatPopover!: Popover;

  @Input() selectedInquiryId: string = '';
  @Output() tabChanged = new EventEmitter<InquiryKeys>();
  @Output() chatSelected = new EventEmitter<string | undefined>();
  @Output() newChatSelected = new EventEmitter<boolean>();
  userFirstName: string = '';

  // Loading states
  loadingInquiries = false;
  loadingDelete = false;

  inquiriesTabs: DkTabProps[] = [
    {
      label:
        'doktar_app.communication.' +
        InquiryTypes.find((type) => type.key === InquiryKeys.TechnicalSupport)?.localizedKey,
      value: InquiryTypes.find((type) => type.key === InquiryKeys.TechnicalSupport)?.id,
    },
  ];
  selectedTab: DkTabProps = this.inquiriesTabs[0];

  allInquiries: InquiryViewModel[] = [];
  renderedContents: { [key: string]: SafeHtml } = {};

  // Date format for inquiry list items
  inquiryDateFormat: DateFormatOptions = {
    showTimeAgo: false,
    simpleFormat: { month: 'long', day: '2-digit' },
  };

  constructor(
    private userService: UserService,
    private _inquiryProxy: InquiryProxy,
    private sanitizer: DomSanitizer
  ) {
    this.userFirstName = this.userService.getUserFirstName();
    this.loadInquiries();
  }

  loadInquiries() {
    this.loadingInquiries = true;
    this._inquiryProxy
      .inquiry_GetAll(InquiryTypes.find((type) => type.id === this.selectedTab.value)?.id)
      .subscribe({
        next: (res) => {
          this.allInquiries = res.result;
          this.processMarkdownForMessages();
          this.loadingInquiries = false;
        },
        error: () => {
          this.loadingInquiries = false;
        },
      });
  }

  changeTab(event: DkTabProps) {
    this.selectedTab = event;

    // Immediately show loading state for better UX
    this.loadingInquiries = true;

    this.tabChanged.emit(InquiryTypes.find((type) => type.id === this.selectedTab.value)?.key);
    this.loadInquiries();
  }

  openDialogForDeleteChat(target: any, inquiryId: string) {
    if (this.deleteChatPopover) {
      this.selectedInquiryId = inquiryId;
      this.deleteChatPopover.toggle(target);
    }
  }

  createInquiry() {
    this.newChatSelected.emit(true);
  }

  selectInquiry(inquiryId: string) {
    this.selectedInquiryId = inquiryId;
    this.chatSelected.emit(inquiryId);
  }

  deleteChat() {
    if (!this.selectedInquiryId) return;

    this.loadingDelete = true;
    this._inquiryProxy.inquiry_Delete(this.selectedInquiryId).subscribe({
      next: () => {
        this.allInquiries = this.allInquiries.filter((inquiry) => inquiry.id !== this.selectedInquiryId);
        this.selectedInquiryId = '';
        this.loadingDelete = false;
        this.deleteChatPopover.hide();
        this.chatSelected.emit(undefined);
      },
      error: () => {
        this.loadingDelete = false;
        this.deleteChatPopover.hide();
      },
    });
  }

  private processMarkdownForMessages(): void {
    this.renderedContents = {};
    if (!this.allInquiries.some((inquiry) => inquiry.inquiryCorrespondences && inquiry.inquiryCorrespondences.length > 0)) {
      return;
    }

    this.allInquiries.forEach((inquiry) => {
      inquiry.inquiryCorrespondences?.forEach((message) => {
        if (message.correspondenceType === 'AI' && message.inquiryContent) {
          const html = marked.parse(message.inquiryContent) as string;
          this.renderedContents[message.id] = this.sanitizer.bypassSecurityTrustHtml(html);
        }
      });
    });
  }

  formatMessageDate(createdAt: DateTime): string {
    if (!createdAt || !createdAt.isValid) {
      return '';
    }

    const now = DateTime.local();
    const messageDate = createdAt.toLocal();

    // Check if year is different
    if (messageDate.year !== now.year) {
      // Format: June 16, 2025 at 09:13
      return messageDate.toFormat('MMMM d, yyyy \'at\' HH:mm');
    }

    if (messageDate.hasSame(now, 'day')) {
      // Format: 09:13
      return messageDate.toFormat('HH:mm');
    }

    // Same year but different day/month
    // Format: September 22, 12:32
    return messageDate.toFormat('MMMM d, HH:mm');
  }
}
